import { useMemo, useRef } from "react"
import { Loader2 } from "lucide-react"
import { useTranslation } from "react-i18next"
import { DrilldownVirtualizedGrid } from "./DrilldownVirtualizedGrid"
import { DrilldownMyBrandCreativeCard } from "./DrilldownMyBrandCreativeCard"
import { mapMyBrandCardRow } from "../lib/drilldown-my-brand-card-mapper"
import { useKpiSelectorCatalog } from "@/features/kpi-settings/hooks/useKpiSelectorCatalog"
import { useTenantCurrency } from "@/lib/use-tenant-currency"
import type { MyBrandCreativeCardModel } from "../lib/drilldown-card.types"
import type { MyBrandFiltersInput } from "@/lib/queries/dashboard.queries"
import type { DrilldownPageFiltersInput } from "@/lib/queries/drilldown.queries"
import { DrilldownCreativeAdSpendPanel } from "./DrilldownCreativeAdSpendPanel"
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll"

interface DrilldownMyBrandGridViewProps {
  /** Accumulated, already globally-sorted rows from every loaded page (do NOT re-sort). */
  rows: Array<Record<string, unknown>>
  fetchNextPage: () => void
  hasNextPage: boolean
  isFetchingNextPage: boolean
  /** Total rows loaded from the server (unfiltered). Used as the pagination recheck key so client-side search filtering doesn't trigger spurious fetches. */
  loadedCount: number
  /** Ordered KPI selection from MyBrandKpiPillRow — index 0 renders "big" on every card. */
  myBrandKpis: string[]
  onOpenDrawer?: (id: string) => void
  showAdSpend?: boolean
  myBrandFilters?: MyBrandFiltersInput
  drilldownFilters?: DrilldownPageFiltersInput
}

export function DrilldownMyBrandGridView({
  rows,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  loadedCount,
  myBrandKpis,
  onOpenDrawer,
  showAdSpend = false,
  myBrandFilters,
  drilldownFilters,
}: DrilldownMyBrandGridViewProps) {
  const { t } = useTranslation()
  const { myBrandById } = useKpiSelectorCatalog("drilldown")
  const { symbol: currencySymbol } = useTenantCurrency()
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)

  // Backend returns one globally-sorted stream paginated by offset, so pages
  // append in order — map straight through, never re-sort (that would make
  // later pages jump above earlier ones).
  const items = useMemo<MyBrandCreativeCardModel[]>(
    () => rows.map((row, index) => mapMyBrandCardRow(row, myBrandKpis, myBrandById, index, currencySymbol)),
    [rows, myBrandKpis, myBrandById, currencySymbol],
  )

  useInfiniteScroll(
    sentinelRef,
    scrollContainerRef,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    { recheckKey: loadedCount },
  )

  return (
    <div ref={scrollContainerRef} className="min-h-0 flex-1 overflow-auto">
      <DrilldownVirtualizedGrid
        items={items}
        emptyLabel={t("drilldown.noCreativesMatch", "No creatives match the applied filters.")}
        scrollContainerRef={scrollContainerRef}
        getKey={(item) => item.id}
        renderItem={(item) => (
          <DrilldownMyBrandCreativeCard
            creative={item}
            onOpenDrawer={onOpenDrawer ? () => onOpenDrawer(item.id) : undefined}
            adSpend={
              showAdSpend && item.hash && myBrandFilters && drilldownFilters ? (
                <DrilldownCreativeAdSpendPanel
                  hash={item.hash}
                  grouping={item.grouping}
                  adsCount={item.adsCount}
                  variant="collapsible"
                  myBrandFilters={myBrandFilters}
                  drilldownFilters={drilldownFilters}
                  currencySymbol={currencySymbol}
                />
              ) : undefined
            }
          />
        )}
      />
      {hasNextPage && (
        <div ref={sentinelRef} className="flex items-center justify-center py-6">
          {isFetchingNextPage && (
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          )}
        </div>
      )}
    </div>
  )
}
